import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Plus, Home, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CustomerOrderSuccess() {
  const { tableId, orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSplitBill = searchParams.get('split') === 'true';
  
  const [order, setOrder] = useState(null);
  const [splitOrders, setSplitOrders] = useState([]);
  const [currentSplitIndex, setCurrentSplitIndex] = useState(0);
  const [table, setTable] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (isSplitBill) {
      fetchSplitOrders();
    } else {
      fetchOrderDetails();
    }
    fetchTableDetails();
  }, [orderId, tableId, isSplitBill]);

  const fetchSplitOrders = async () => {
    try {
      console.log('Fetching split orders for parent_order_id:', orderId);
      const response = await axios.get(`${API}/orders/split/${orderId}`);
      console.log('Split orders response:', response.data);
      console.log('Number of split orders:', response.data.length);
      setSplitOrders(response.data);
      if (response.data.length > 0) {
        setOrder(response.data[0]); // Show first order by default
      }
    } catch (error) {
      console.error('Error fetching split orders:', error);
      toast.error('Could not load split bill details');
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API}/public/orders/${orderId}?table_id=${tableId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Could not load order details');
    }
  };

  const fetchTableDetails = async () => {
    try {
      const response = await axios.get(`${API}/public/tables/${tableId}`);
      setTable(response.data);
    } catch (error) {
      console.error('Error fetching table:', error);
    }
  };

  const addMoreItems = () => {
    const orderIdToUse = isSplitBill ? splitOrders[0]?.id : orderId;
    navigate(`/order/${tableId}?existing_order=${orderIdToUse}`);
  };

  const handlePrevOrder = () => {
    if (currentSplitIndex > 0) {
      const newIndex = currentSplitIndex - 1;
      setCurrentSplitIndex(newIndex);
      setOrder(splitOrders[newIndex]);
      scrollToCard(newIndex);
    }
  };

  const handleNextOrder = () => {
    if (currentSplitIndex < splitOrders.length - 1) {
      const newIndex = currentSplitIndex + 1;
      setCurrentSplitIndex(newIndex);
      setOrder(splitOrders[newIndex]);
      scrollToCard(newIndex);
    }
  };

  const scrollToCard = (index) => {
    if (scrollContainerRef.current) {
      const cardWidth = 280; // Approximate width of each card
      scrollContainerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 p-4">
      {isSplitBill && splitOrders.length > 0 ? (
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Split Bill Orders Placed!</h1>
            <p className="text-gray-600">{splitOrders.length} separate orders created</p>
          </div>

          {/* Scroll view of invoices */}
          <div className="relative mb-6 px-4">
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto p-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'thin' }}
            >
              {splitOrders.map((splitOrder, idx) => (
                <Card 
                  key={splitOrder.id} 
                  className={`flex-shrink-0 w-72 shadow-lg snap-center cursor-pointer transition-transform ${
                    idx === currentSplitIndex ? 'ring-4 ring-blue-500 scale-105' : 'opacity-75 hover:opacity-100'
                  }`}
                  onClick={() => {
                    setCurrentSplitIndex(idx);
                    setOrder(splitOrder);
                  }}
                >
                  <CardContent className="p-6 h-[500px] flex flex-col">
                    <div className="text-center mb-4">
                      <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Invoice {idx + 1} of {splitOrders.length}
                      </div>
                      <h3 className="font-bold text-lg">{splitOrder.split_person_name || splitOrder.customer_name}</h3>
                      <p className="text-sm text-gray-500">{splitOrder.split_person_phone || splitOrder.customer_phone}</p>
                      <p className="text-xs text-blue-600 font-semibold mt-1">
                        Split Bill: 1/{splitOrders.length} share
                      </p>
                    </div>

                    {/* Scrollable content area */}
                    <div className="flex-1 overflow-y-auto mb-3">
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="space-y-3">
                          {splitOrder.items?.map((item, itemIdx) => {
                            // Calculate original price (split price * number of people)
                            const splitPrice = item.price * item.quantity;
                            const originalPrice = splitPrice * splitOrders.length;
                            
                            return (
                              <div key={itemIdx} className="border-b pb-2 last:border-0">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="font-medium">{item.quantity}x {item.item_name}</span>
                                  <span className="font-semibold text-blue-600">${splitPrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 italic">
                                  <span>Original price</span>
                                  <span className="line-through">${originalPrice.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-blue-600 mt-1">
                                  Your share: 1/{splitOrders.length}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Grand Total */}
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-3 mb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-sm text-gray-600">
                            <span>Original Total:</span>
                            <span className="line-through">${(splitOrder.grand_total * splitOrders.length).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center font-bold text-lg border-t pt-2">
                            <span>Your Share ({idx + 1}/{splitOrders.length})</span>
                            <span className="text-green-600">${splitOrder.grand_total?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Order ID */}
                      <p className="text-xs text-center text-gray-500 mb-3">
                        Order ID: {splitOrder.id.slice(0, 8)}
                      </p>

                      {/* Tip section */}
                      <div className="bg-yellow-50 rounded-lg p-3 mb-2">
                        <p className="text-xs text-gray-700">
                          💡 <span className="font-semibold">Split Bill:</span> This is {splitOrder.split_person_name || 'this person'}'s share
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Navigation arrows */}
            {splitOrders.length > 1 && (
              <>
                <button
                  onClick={handlePrevOrder}
                  disabled={currentSplitIndex === 0}
                  className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={handleNextOrder}
                  disabled={currentSplitIndex === splitOrders.length - 1}
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {table && (
            <div className="max-w-lg mx-auto mb-6 p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Table:</span> {table.table_name}
              </p>
            </div>
          )}

          <div className="max-w-lg mx-auto space-y-3">
            <Button
              onClick={() => navigate(`/order/${tableId}`)}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-6"
            >
              <Home className="mr-2" />
              Back to Menu
            </Button>
          </div>
        </div>
      ) : (
        // Original single order view
        <div className="max-w-lg mx-auto">
          <Card className="shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Order Placed!</h1>
                <p className="text-gray-600">Your order has been sent to the kitchen</p>
              </div>

              {order && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left">
                  <h2 className="font-semibold text-lg mb-3">Order Summary</h2>
                  <div className="space-y-2">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>
                          {item.quantity}x {item.item_name}
                          {item.modifiers && item.modifiers.length > 0 && (
                            <span className="text-gray-500 text-xs block ml-4">
                              {item.modifiers.map(m => m.option_name).join(', ')}
                            </span>
                          )}
                        </span>
                        <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-green-600">${order.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {table && (
                <div className="mb-6 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Table:</span> {table.table_name}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Order ID:</span> {orderId?.slice(0, 8)}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={addMoreItems}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-6"
                >
                  <Plus className="mr-2" />
                  Add More Items
                </Button>
                
                <Button
                  onClick={() => navigate(`/order/${tableId}`)}
                  variant="outline"
                  className="w-full py-6"
                >
                  <Home className="mr-2" />
                  Back to Menu
                </Button>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  💡 <span className="font-semibold">Tip:</span> Your order will be prepared shortly. You can add more items to this order anytime!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
